"use client";

import type React from "react";
import { createContext, useEffect } from "react";
import { useAudio, useLocalStorage } from "react-use";

interface AudioContextType {
  isMuted: boolean;
  toggleMute: () => void;
  playCorrect: () => void;
  audio: React.JSX.Element;
  audioVoice: React.JSX.Element;
  refVoice: HTMLAudioElement | null;
}

export const AudioContext = createContext<AudioContextType | undefined>(
  undefined,
);

export const AudioProvider = ({ children }: { children: React.ReactNode }) => {
  const [audio, _state, controls, _ref] = useAudio({ src: "/correct.wav" });
  const [audioVoice, _stateVoice, controlsVoice, refVoice] = useAudio({
    src: "/correct-voice-mode.wav",
  });

  const [isMuted, setIsMuted] = useLocalStorage<boolean>("isMuted", false);

  // Sync the audio state with localStorage on mount and when isMuted changes
  useEffect(() => {
    if (isMuted ?? false) {
      controls.mute();
      controlsVoice.mute();
    } else {
      controls.unmute();
      controlsVoice.unmute();
    }
  }, [isMuted, controls, controlsVoice]);

  const toggleMute = () => {
    setIsMuted(!(isMuted ?? false));
  };

  const playCorrect = () => {
    if (!(isMuted ?? false)) {
      controls.play();
    }
  };

  return (
    <AudioContext.Provider
      value={{
        isMuted: isMuted ?? false,
        toggleMute,
        playCorrect,
        audio,
        audioVoice,
        refVoice: refVoice.current,
      }}
    >
      {children}
      {audio}
      {audioVoice}
    </AudioContext.Provider>
  );
};
